<template>
    <section class="slider-block">
        <div class="inner">
            <DSCarousel
                :slides="slides"
                :loop="true"
                :autoplay-timeout-ms="autoplay"
                progressbars
                show-pause-button
            >
                <div
                    v-for="(slide, index) in slides"
                    :key="index"
                    class="panel"
                >
                    <div
                        class="slide"
                        :class="{
                            [$cms.computedBackgroundClass(display, layout?.name)]: true,
                        }"
                    >
                        <div class="slide-info">
                            <h4 v-if="slide.byline" class="h6 byline">{{ slide.byline }}</h4>
                            <h2 class="h2">{{ slide.title }}</h2>
                            <DSCFText
                                v-if="typeof(slide.text) === 'object' && slide.text?.nodeType === 'document'"
                                :content="slide.text"
                            />
                            <p v-else-if="slide.text">{{ slide.text }}</p>
                            <div
                                v-if="slide.links"
                                class="slide-links"
                            >
                                <NuxtLink
                                    v-for="(link,i) in slide.links.filter(link => link.title)"
                                    :key="link.id"
                                    class="ds-btn ds-btn--large"
                                    :class="i === 0 ? 'ds-btn--primary' : 'ds-btn--secondary'"
                                    v-bind="$cms.parseLink(link)"
                                >
                                    {{ link.title }}
                                </NuxtLink>
                            </div>
                        </div>
                        <DSFrontifyAsset class="image" :asset="slide.img" />
                    </div>
                </div>
            </DSCarousel>
        </div>
    </section>
</template>
<script setup>
import { inject, useNuxtApp, computed } from '#imports';

const { $cms, $dsc } = useNuxtApp();
const layout = inject('layout');

const props = defineProps({
    autoplay: {
        type: Number,
        required: false,
        default: 8000,
    },
    contentType: {
        type: String,
        required: false,
        default: 'cardList',
    },
    internal: {
        type: String,
        required: false,
        default: '',
    },
    display: {
        type: Object,
        required: false,
        default: () => ({
            component: 'slider',
            background: 'primary',
        }),
    },
    title: {
        type: String,
        required: false,
        default: '',
    },
    cards: {
        type: Array,
        required: false,
        default: () => [],
    },
});

const slides = computed(() => props.cards.map(card => ({
    img: card.image,
    byline: card.byline,
    title: card.title,
    text: typeof(card.text) === 'object' && $dsc.object.isEmpty(card.text) ? false : card.text,
    links: card.links,
})) ?? []);
</script>
<style>
.slider-block {
  display: grid;
  max-width: var(--desktop-max-width);
  width: 100%;
  margin: 0 auto;
}
.slider-block .inner {
  justify-self: end;
  padding-left: 1.5rem;
  width: 100%;
}
.slider-block .inner {
  position: relative;
  max-width: 100%;
  overflow: hidden;
  padding-left: 0;
}
.slider-block .slide.bg-primary {
  background: var(--color-primary-500);
}
.slider-block .slide.bg-secondary {
  background: var(--color-secondary-500);
}
.slider-block .slide.bg-tertiary {
  background: var(--color-tertiary-500);
}
.slider-block .slide.fg-light .h1,
.slider-block .slide.fg-light .h2,
.slider-block .slide.fg-light .h3,
.slider-block .slide.fg-light .h4,
.slider-block .slide.fg-light .h5,
.slider-block .slide.fg-light .h6,
.slider-block .slide.fg-light .rich-text,
.slider-block .slide.fg-light .byline {
  color: var(--color-base-white);
}
.slider-block .slide.fg-light .ds-btn--primary {
  background: var(--color-secondary-500);
  color: var(--color-base-black);
}
.slider-block .slide.fg-light .ds-btn--primary:hover {
  background: var(--color-base-white);
  color: var(--color-base-black);
}
.slider-block .slide.fg-light .ds-btn--secondary {
  background: none;
  color: var(--color-base-white);
  border-color: var(--color-base-white);
}
.slider-block .slide.fg-light .ds-btn--secondary:hover {
  background: var(--color-base-white);
  color: var(--color-base-black);
}
.slider-block .slide {
  display: grid;
  width: 100%;
  grid-template-columns: 42.4% 57.6%;
  padding: 0;
}
.slider-block .slide .image {
  grid-column-start: 2;
  width: 100%;
  padding-top: 66.6666%;
}
@media (max-width: 991px) {
  .slider-block .slide .image {
    padding-top: 75%;
  }
}
.slider-block .slide .slide-info {
  box-sizing: border-box;
  display: block;
  height: 100%;
  margin: 0;
  padding: 3rem;
  grid-column-start: 1;
  justify-self: start;
  align-self: center;
}
.slider-block .slide .slide-info h3 {
  font-weight: 800;
  font-size: 2.25rem;
}
.slider-block .slide .slide-info p {
  padding: 0;
  margin: 1rem 0 2rem;
}
.slider-block .slide .slide-info li p {
  margin: 0.25rem;
}
.slider-block .slide .slide-info .slide-links {
  margin-bottom: -0.5rem;
}
.slider-block .slide .slide-info .slide-links * {
  margin-bottom: 0.5rem;
}
.slider-block .slide .slide-info .slide-links :not(:last-child) {
  margin-right: 1rem;
}
.slider-block .card.bg-primary {
  background: var(--color-primary-500);
}
.slider-block .card.bg-secondary {
  background: var(--color-secondary-500);
}
.slider-block .card.bg-tertiary {
  background: var(--color-tertiary-500);
}
.slider-block .card.fg-light .h1,
.slider-block .card.fg-light .h2,
.slider-block .card.fg-light .h3,
.slider-block .card.fg-light .h4,
.slider-block .card.fg-light .h5,
.slider-block .card.fg-light .h6,
.slider-block .card.fg-light .rich-text,
.slider-block .card.fg-light .byline {
  color: var(--color-base-white);
}
.slider-block .card.fg-light .ds-btn--primary {
  background: var(--color-secondary-500);
  color: var(--color-base-black);
}
.slider-block .card.fg-light .ds-btn--primary:hover {
  background: var(--color-base-white);
  color: var(--color-base-black);
}
.slider-block .card.fg-light .ds-btn--secondary {
  background: none;
  color: var(--color-base-white);
  border-color: var(--color-base-white);
}
.slider-block .card.fg-light .ds-btn--secondary:hover {
  background: var(--color-base-white);
  color: var(--color-base-black);
}
.slider-block .bg-custom {
  background-color: v-bind("display.background");
}
.slider-block .inner {
  position: relative;
  max-width: 100%;
  overflow: hidden;
  padding-left: 0;
}
.slider-block .controls .btn-previous,
.slider-block .controls .btn-next,
.slider-block .controls .btn-pause {
  width: 2rem;
  height: 2rem;
  border-radius: 100%;
  background-color: rgb(255, 255, 255);
  display: grid;
  justify-content: center;
  align-content: center;
}
.slider-block .controls .btn-previous .icon,
.slider-block .controls .btn-next .icon,
.slider-block .controls .btn-pause .icon {
  width: 1rem;
  height: 1rem;
}
.slider-block .controls .btn-previous {
  left: 0.25rem;
}
.slider-block .controls .btn-next {
  right: 0.25rem;
}
@media (min-width: 992px) {
  .slider-block .controls .btn-next,
  .slider-block .controls .btn-previous {
    display: none;
  }
}
@media (max-width: 1199px) {
  .slider-block .slide {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
    min-height: 100%;
  }
  .slider-block .slide .slide-info {
    grid-column-start: 1;
    grid-row-start: 2;
    width: 100%;
    position: relative;
    align-content: start;
  }
  .slider-block .slide > *, .slider-block .slide .image {
    grid-column-start: 1;
  }
}
</style>
